import React, { useCallback, useMemo, useRef } from "react";
import { useFormState } from "react-final-form";
import { Box } from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query";

import { TreeEnvironmentRef, TreeItem } from "ui/Tree";

import { useGetVersionConfigQuery } from "fond/api";
import BaseLegend from "fond/map/Legend/BaseLegend";
import { InferredIcon } from "fond/map/Legend/InferredIcon";
import { BaseMultiProject, GroupConfig, MapLayerConfig, Project, Version } from "fond/types";
import { LayerConfig, LayerStyle, SublayerConfig } from "fond/types/ProjectLayerConfig";
import { generateTreeItems } from "fond/utils/configurations";
import { BlockSpinner } from "fond/widgets";

import { PreviewContainer } from "./VersionLayersPreview.style";

type LegendItemType = MapLayerConfig | LayerConfig | SublayerConfig | LayerStyle | GroupConfig;

interface IFormData {
  Name: string;
  Project: Project | BaseMultiProject | "";
  Version: Version;
}

/**
 * Component that displays the layer tree based on form values
 */
const VersionLayersPreview: React.FC = () => {
  const formState = useFormState<IFormData>();
  const versionId = formState.values.Version?.ID;
  const environmentRef = useRef<TreeEnvironmentRef>(undefined);

  const {
    data: config,
    isLoading,
    isFetching,
  } = useGetVersionConfigQuery(versionId || skipToken, {
    skip: !versionId,
  });

  // Filter out projectComments from the configuration
  const filteredConfig = useMemo(() => {
    if (!config) return null;

    // Filter projectComments from MapChildren
    const filteredMapChildren = config.MapChildren.filter((childId) => childId !== "projectComments");

    return {
      ...config,
      MapChildren: filteredMapChildren,
    };
  }, [config]);

  // Generate tree items from filtered configuration
  const treeItems = useMemo(() => (filteredConfig ? generateTreeItems({ config: filteredConfig, showStyles: false }) : {}), [filteredConfig]);

  // Get item title based on type
  const getItemTitle = useCallback(({ data: itemData }: TreeItem<LegendItemType>) => {
    if (itemData.Type === "STYLE") return itemData.Name;
    if (itemData.Type === "MapLayerConfig") return "";
    return itemData.Label;
  }, []);

  // Render layer icons
  const renderItemLeftAdornment = useCallback(
    ({ data }: TreeItem<LegendItemType>) => {
      if (data.Type !== "GROUP" && data.Type !== "MapLayerConfig" && filteredConfig) {
        return <InferredIcon entity={data as LayerConfig | SublayerConfig | LayerStyle} config={filteredConfig} />;
      }
      return null;
    },
    [filteredConfig]
  );

  // Expand all items by default
  const defaultExpanded = useMemo(() => {
    if (!filteredConfig) return [];
    const expanded: string[] = [];
    Object.keys(treeItems).forEach((key) => {
      expanded.push(key);
    });
    return expanded;
  }, [filteredConfig, treeItems]);

  const expand = useMemo(() => ({ defaultExpanded }), [defaultExpanded]);

  const isLoadingState = isLoading || isFetching;

  if (isLoadingState) {
    return (
      <Box sx={{ width: 250, display: "flex", alignItems: "center", justifyContent: "center", p: 2 }}>
        <BlockSpinner />
      </Box>
    );
  }

  if (!versionId || !filteredConfig) {
    return (
      <PreviewContainer>
        <Box sx={{ color: "text.secondary", p: 2, fontSize: "0.875rem" }}>Select a version to see available layers</Box>
      </PreviewContainer>
    );
  }

  return (
    <PreviewContainer className="customScrollbars">
      <BaseLegend
        environmentRef={environmentRef}
        treeId="version-layers-preview"
        treeItems={treeItems}
        getItemTitle={getItemTitle}
        getItemTitleClass={(item) => `rct-tree-item-button-${item.data?.Type}`}
        getItemClass={(item) => `rct-tree-item-title-${item.data?.Type}`}
        getItemVisibility={() => true}
        renderItemLeftAdornment={renderItemLeftAdornment}
        onItemVisibilityChanges={() => {}}
        expand={expand}
        showTabHeader={false}
      />
    </PreviewContainer>
  );
};

export default VersionLayersPreview;
